import {
	type GetOrganizationActiveActivitiesQuery,
	OrderQuoteStatus,
	OrderStatus,
	OrganizationActivityType,
	useGetOrderQuoteStatsByStatusQuery,
	useGetOrderStatsByStatusQuery,
	useGetOrganizationActiveActivitiesQuery,
	useGetRecentOrderQuotesQuery,
	useGetRecentOrdersQuery,
} from '@codezo/graphql';
import {
	ArrowRight,
	ArrowUpRight,
	ChevronRight,
	CreditCard,
	FileText,
	Package,
	Truck,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { CurrencyText } from '../../components/CurrencyText';
import { Card } from '../../components/ui/card';
import { SectionCard } from '../../components/ui/section-card';
import { StatusBadge } from '../../components/ui/status-badge';
import { useAuth } from '../../hooks/useAuth';
import { formatDate } from '../../lib/utils';
import { useCurrencyStore } from '../../stores/currency';

/* ── Status configs ── */

type Tone = 'success' | 'warn' | 'info' | 'neutral' | 'danger';

const QUOTE_STATUS: Record<string, { label: string; tone: Tone; color: string }> = {
	[OrderQuoteStatus.Draft]: {
		label: 'Draft',
		tone: 'neutral',
		color: 'var(--color-status-neutral)',
	},
	[OrderQuoteStatus.Pending]: {
		label: 'Pending',
		tone: 'warn',
		color: 'var(--color-status-warn)',
	},
	[OrderQuoteStatus.Submitted]: {
		label: 'Submitted',
		tone: 'info',
		color: 'var(--color-status-info)',
	},
	[OrderQuoteStatus.Accepted]: {
		label: 'Accepted',
		tone: 'success',
		color: 'var(--color-status-success)',
	},
	[OrderQuoteStatus.Rejected]: {
		label: 'Rejected',
		tone: 'danger',
		color: 'var(--color-status-danger)',
	},
	[OrderQuoteStatus.Expired]: {
		label: 'Expired',
		tone: 'neutral',
		color: 'var(--color-status-neutral)',
	},
};

const ORDER_STATUS: Record<string, { label: string; tone: Tone; color: string }> = {
	[OrderStatus.Pending]: { label: 'Pending', tone: 'warn', color: 'var(--color-status-warn)' },
	[OrderStatus.InProduction]: {
		label: 'In Production',
		tone: 'info',
		color: 'var(--color-status-info)',
	},
	[OrderStatus.Completed]: {
		label: 'Completed',
		tone: 'success',
		color: 'var(--color-status-success)',
	},
	[OrderStatus.Cancelled]: {
		label: 'Cancelled',
		tone: 'danger',
		color: 'var(--color-status-danger)',
	},
};

/* ── Helpers ── */

function timeAgo(date: string | Date): string {
	const now = Date.now();
	const then = new Date(date).getTime();
	const diff = Math.max(0, now - then);
	const minutes = Math.floor(diff / 60000);
	if (minutes < 1) return 'Just now';
	if (minutes < 60) return `${minutes}m ago`;
	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ago`;
	const days = Math.floor(hours / 24);
	if (days < 7) return `${days}d ago`;
	return formatDate(date);
}

function getGreeting(): string {
	const h = new Date().getHours();
	if (h < 12) return 'Good morning';
	if (h < 18) return 'Good afternoon';
	return 'Good evening';
}

/* ── Distribution bar ── */

function DistributionBar({
	segments,
}: {
	segments: Array<{
		key: string;
		label: string;
		value: number;
		percentage: number;
		color: string;
	}>;
}) {
	const total = segments.reduce((s, seg) => s + seg.value, 0);
	if (total === 0) return null;

	return (
		<div className="px-4 py-4">
			<div className="mb-3 flex h-1.5 overflow-hidden rounded-full bg-surface-fill">
				{segments.map((seg) =>
					seg.percentage > 0 ? (
						<div
							key={seg.key}
							className="transition-all duration-300"
							style={{
								width: `${seg.percentage}%`,
								backgroundColor: seg.color,
								opacity: 0.8,
							}}
						/>
					) : null
				)}
			</div>
			<div className="flex flex-wrap gap-x-5 gap-y-1.5">
				{segments.map((seg) => (
					<div key={seg.key} className="flex items-center gap-1.5">
						<span
							className="h-2 w-2 shrink-0 rounded-full"
							style={{ backgroundColor: seg.color }}
						/>
						<span className="text-xs text-text-3">{seg.label}</span>
						<span className="font-mono text-xs font-medium text-text-1">
							{seg.value}
						</span>
					</div>
				))}
			</div>
		</div>
	);
}

/* ── Activity type helpers (organization-specific) ── */

type OrganizationActivityItem = NonNullable<
	GetOrganizationActiveActivitiesQuery['getOrganizationActiveActivities']
>['activities'][number];

/** type → icon. The label itself is the backend-provided `title`. */
const ACTIVITY_ICON: Record<string, React.ElementType> = {
	[OrganizationActivityType.DraftQuote]: FileText,
	[OrganizationActivityType.QuoteOfferClientReview]: Package,
	[OrganizationActivityType.OrderSamplePaymentPending]: CreditCard,
	[OrganizationActivityType.OrderSampleApprovalPending]: Package,
};

function getActivityInfo(
	activity: OrganizationActivityItem
): { label: string; sub: string; time?: string; path: string; icon: React.ElementType } | null {
	switch (activity.__typename) {
		case 'OrganizationQuoteActivity':
			return {
				label: activity.title ?? 'Quote needs attention',
				sub: 'Quote',
				path: `/quotes/${activity.quoteId}`,
				icon: ACTIVITY_ICON[activity.type] ?? FileText,
			};
		case 'OrganizationOrderActivity':
			return {
				label: activity.title ?? 'Order needs attention',
				sub: 'Order',
				path: `/orders/${activity.orderId}`,
				icon: ACTIVITY_ICON[activity.type] ?? Truck,
			};
		case 'OrganizationOrderQuoteOfferBatchActivity':
			return {
				label: activity.title ?? 'Offer to review',
				sub: activity.batch?.name || 'Offer',
				path: activity.batch?.orderQuoteId ? `/quotes/${activity.batch.orderQuoteId}` : '#',
				icon: ACTIVITY_ICON[activity.type] ?? Package,
			};
		default:
			return null;
	}
}

/* ── Loading skeleton ── */

function DashboardSkeleton() {
	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			<div className="mb-1 h-8 w-56 animate-pulse rounded bg-surface-fill" />
			<div className="mb-8 h-4 w-80 animate-pulse rounded bg-surface-fill" />
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
				{Array.from({ length: 4 }).map((_, i) => (
					<div
						key={i}
						className="h-[220px] animate-pulse rounded-[var(--r-3)] border border-line-1 bg-surface"
					/>
				))}
			</div>
		</div>
	);
}

/* ════════════════════════ DASHBOARD PAGE ════════════════════════ */

export function DashboardPage() {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { selectedCurrency } = useCurrencyStore();

	/* ── Data fetching ── */

	const { data: quoteStats, loading: loadingQuoteStats } = useGetOrderQuoteStatsByStatusQuery({
		fetchPolicy: 'no-cache',
	});

	const { data: orderStatusData } = useGetOrderStatsByStatusQuery({
		variables: { currency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});

	const { data: recentQuotes } = useGetRecentOrderQuotesQuery({
		variables: { limit: 5 },
		fetchPolicy: 'no-cache',
	});

	const { data: recentOrders } = useGetRecentOrdersQuery({
		variables: { limit: 5, currency: selectedCurrency },
		fetchPolicy: 'no-cache',
	});

	const { data: activitiesData } = useGetOrganizationActiveActivitiesQuery({
		variables: { limit: 8 },
		fetchPolicy: 'no-cache',
	});

	/* ── Derived ── */

	const quoteBreakdown = quoteStats?.getOrderQuoteStatsByStatus;
	const orderBreakdown = orderStatusData?.getOrderStatsByStatus;
	const quotes = recentQuotes?.getRecentOrderQuotes ?? [];
	const orders = recentOrders?.getRecentOrders ?? [];
	const activities = activitiesData?.getOrganizationActiveActivities?.activities ?? [];

	if (loadingQuoteStats) return <DashboardSkeleton />;

	const quoteSegments = (quoteBreakdown?.statusBreakdown ?? []).map((s) => {
		const cfg = QUOTE_STATUS[s.status] ?? {
			label: s.status,
			color: 'var(--color-status-neutral)',
		};
		return {
			key: s.status,
			label: cfg.label,
			value: s.count,
			percentage: s.percentage ?? 0,
			color: cfg.color,
		};
	});

	const orderSegments = (orderBreakdown?.statusBreakdown ?? []).map((s) => {
		const cfg = ORDER_STATUS[s.status] ?? {
			label: s.status,
			color: 'var(--color-status-neutral)',
		};
		return {
			key: s.status,
			label: cfg.label,
			value: s.count,
			percentage: s.percentage ?? 0,
			color: cfg.color,
		};
	});

	const userName = user?.organizationUser?.firstName || user?.email?.split('@')[0] || 'there';

	return (
		<div className="mx-auto max-w-[var(--content-max)] px-4 pt-6 pb-20 sm:px-6 md:px-8 md:pt-8">
			{/* ── Welcome header ── */}
			<div className="mb-8">
				<h1 className="mb-1 text-2xl font-medium leading-tight -tracking-[0.02em] text-text-1 sm:text-3xl">
					{getGreeting()}, {userName}
				</h1>
				<p className="text-base text-text-3">
					Here’s what’s happening across your organization today.
				</p>
			</div>

			{/* ── Distribution bars ── */}
			<div className="mb-7 grid grid-cols-1 gap-3 lg:grid-cols-2">
				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="flex items-center justify-between px-4 py-3">
						<span className="text-sm font-medium uppercase tracking-[0.04em] text-text-1">
							Quotes
						</span>
						<span className="font-mono text-base font-medium text-text-1">
							{quoteBreakdown?.total ?? 0}
						</span>
					</div>
					{(quoteBreakdown?.total ?? 0) > 0 ? (
						<DistributionBar segments={quoteSegments} />
					) : (
						<div className="flex items-center gap-3 px-4 pb-4">
							<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line-1 bg-surface-fill">
								<FileText size={14} className="text-text-4" />
							</div>
							<div>
								<div className="text-sm text-text-3">No quotes yet</div>
								<div className="text-xs text-text-4">
									Quotes will appear here once created.
								</div>
							</div>
						</div>
					)}
				</Card>

				<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
					<div className="flex items-center justify-between px-4 py-3">
						<span className="text-sm font-medium uppercase tracking-[0.04em] text-text-1">
							Orders
						</span>
						<span className="font-mono text-base font-medium text-text-1">
							{orderBreakdown?.total ?? 0}
						</span>
					</div>
					{(orderBreakdown?.total ?? 0) > 0 ? (
						<DistributionBar segments={orderSegments} />
					) : (
						<div className="flex items-center gap-3 px-4 pb-4">
							<div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-line-1 bg-surface-fill">
								<Truck size={14} className="text-text-4" />
							</div>
							<div>
								<div className="text-sm text-text-3">No orders yet</div>
								<div className="text-xs text-text-4">
									Orders will appear here once quotes are accepted.
								</div>
							</div>
						</div>
					)}
				</Card>
			</div>

			{/* ── Main content ── */}
			<div className="grid grid-cols-1 gap-4 lg:grid-cols-[1.6fr_1fr]">
				{/* LEFT */}
				<div className="flex flex-col gap-4">
					{/* Recent quotes */}
					<SectionCard
						title="Recent quotes"
						action={
							<button onClick={() => navigate('/quotes')}>
								View all <ArrowRight size={11} />
							</button>
						}
					>
						{quotes.length === 0 ? (
							<div className="flex flex-col items-center gap-3 py-12 text-center">
								<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
									<FileText size={18} />
								</div>
								<div className="text-sm text-text-3">No recent quotes</div>
							</div>
						) : (
							<div>
								{quotes.map((q, i) => {
									const config = QUOTE_STATUS[q.status] ?? {
										label: q.status,
										tone: 'neutral' as const,
									};
									return (
										<div
											key={q.id}
											className="flex cursor-pointer items-center gap-3.5 px-4 py-3 transition-colors duration-[180ms] hover:bg-surface-hover"
											style={
												i < quotes.length - 1
													? { borderBottom: '0.5px solid var(--line-1)' }
													: undefined
											}
											onClick={() => navigate(`/quotes/${q.id}`)}
										>
											<div className="min-w-0 flex-1">
												<div className="mb-0.5 text-base font-medium text-text-1">
													{q.name}
												</div>
												<div className="text-sm text-text-4">
													{q.organization?.name}
													{q.createdAt && ` · ${timeAgo(q.createdAt)}`}
												</div>
											</div>
											<StatusBadge
												active
												activeLabel={config.label}
												activeTone={config.tone}
											/>
											<ChevronRight size={14} className="text-text-4" />
										</div>
									);
								})}
							</div>
						)}
					</SectionCard>

					{/* Recent orders */}
					<SectionCard
						title="Recent orders"
						action={
							<button onClick={() => navigate('/orders')}>
								View all <ArrowRight size={11} />
							</button>
						}
					>
						{orders.length === 0 ? (
							<div className="flex flex-col items-center gap-3 py-12 text-center">
								<div className="flex h-10 w-10 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3">
									<Truck size={18} />
								</div>
								<div className="text-sm text-text-3">No recent orders</div>
							</div>
						) : (
							<div>
								{orders.map((o, i) => {
									const config = ORDER_STATUS[o.status] ?? {
										label: o.status,
										tone: 'neutral' as const,
									};
									return (
										<div
											key={o.id}
											className="flex cursor-pointer items-center gap-3.5 px-4 py-3 transition-colors duration-[180ms] hover:bg-surface-hover"
											style={
												i < orders.length - 1
													? { borderBottom: '0.5px solid var(--line-1)' }
													: undefined
											}
											onClick={() => navigate(`/orders/${o.id}`)}
										>
											<div className="min-w-0 flex-1">
												<div className="mb-0.5 text-base font-medium text-text-1">
													{o.name}
												</div>
												<div className="text-sm text-text-4">
													{o.organization?.name}
													{o.createdAt && ` · ${timeAgo(o.createdAt)}`}
												</div>
											</div>
											{o.totalAmount != null && (
												<CurrencyText
													amount={o.totalAmount}
													className="text-sm text-text-2"
												/>
											)}
											<StatusBadge
												active
												activeLabel={config.label}
												activeTone={config.tone}
											/>
											<ChevronRight size={14} className="text-text-4" />
										</div>
									);
								})}
							</div>
						)}
					</SectionCard>
				</div>

				{/* RIGHT — Activity feed + Quick links */}
				<div className="flex flex-col gap-4">
					<SectionCard title="Activity">
						{activities.length === 0 ? (
							<div className="flex flex-col items-center gap-3 py-14 text-center">
								<div
									className="flex h-11 w-11 items-center justify-center rounded-full border border-line-1 bg-surface-fill text-text-3"
									style={{ opacity: 0.6 }}
								>
									<FileText size={18} />
								</div>
								<div className="text-base text-text-2">All caught up</div>
								<div className="text-sm text-text-4">
									Activity from quotes and orders will appear here.
								</div>
							</div>
						) : (
							<div>
								{activities.map((activity, i) => {
									const info = getActivityInfo(activity);
									if (!info) return null;
									const Icon = info.icon;
									return (
										<div
											key={activity.id}
											className="flex cursor-pointer items-start gap-3 px-4 py-3 transition-colors duration-[180ms] hover:bg-surface-hover"
											style={
												i < activities.length - 1
													? { borderBottom: '0.5px solid var(--line-1)' }
													: undefined
											}
											onClick={() => navigate(info.path)}
										>
											<div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-line-1 bg-surface-fill">
												<Icon size={12} className="text-text-3" />
											</div>
											<div className="min-w-0 flex-1">
												<div className="text-sm font-medium text-text-1">
													{info.label}
												</div>
												<div className="text-xs text-text-4">
													{info.sub}
												</div>
											</div>
											{info.time && (
												<span className="shrink-0 pt-0.5 text-2xs text-text-4">
													{timeAgo(info.time)}
												</span>
											)}
										</div>
									);
								})}
							</div>
						)}
					</SectionCard>

					{/* Quick links */}
					<Card className="overflow-hidden border-line-1 bg-surface gap-0 py-0">
						<div className="px-4 py-3">
							<span className="text-sm font-medium uppercase tracking-[0.04em] text-text-1">
								Quick links
							</span>
						</div>
						{[
							{ label: 'Browse products', path: '/products' },
							{ label: 'Manage brands', path: '/profile#brands' },
							{ label: 'Organization settings', path: '/settings' },
						].map((link, i) => (
							<div
								key={link.path}
								className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition-colors duration-[180ms] hover:bg-surface-hover"
								style={
									i < 3 ? { borderTop: '0.5px solid var(--line-1)' } : undefined
								}
								onClick={() => navigate(link.path)}
							>
								<span className="flex-1 text-sm text-text-2">{link.label}</span>
								<ArrowUpRight size={12} className="text-text-4" />
							</div>
						))}
					</Card>
				</div>
			</div>
		</div>
	);
}
